import React, { FC, ElementType } from 'react'
import every from 'lodash/every'
import some from 'lodash/some'
import { createStyles, makeStyles, StepLabel } from 'transfix-ui/core'
import { SidebarEmptyIcon, SidebarCheckIcon, SidebarErrorIcon } from './SidebarStepIcon'

type TStepLabelClassKey = 'label' | 'iconContainer'
interface IUseStepLabelStylesProps {
  error: boolean
  completed: boolean
  isActive: boolean
}

const useStepLabelStyles = makeStyles(theme =>
  createStyles<TStepLabelClassKey, IUseStepLabelStylesProps>({
    label: {
      fontFamily: theme.typography.fontFamily,
      fontSize: 16,
      lineHeight: 1.375,
      color: props => (props.error ? '#bd1a1d' : 'inherit'),
      fontWeight: props => (props.error || props.isActive ? 'bold' : 'inherit'),
      '&.Mui-active': {
        color: props => (props.error ? '#bd1a1d' : 'inherit'),
        fontWeight: props => (props.error || props.isActive ? 'bold' : 'inherit'),
      },
    },
    iconContainer: {
      paddingRight: theme.spacing(2),
    },
  })
)

interface IStatus {
  isCompleted: boolean
  error: boolean
}

const getStatus = (values: Record<string, any>, errors: Record<string, any>): IStatus => {
  return { isCompleted: every(values, undefined), error: some(errors, String) }
}

interface IGetIconProps {
  isCompleted: boolean
  statusError: boolean
  fieldsLength: number
}

/**
 * Programmatically selects an icon to be displayed for each step, based on whether it's
 * a valid and/or completed item
 */
const getIcon = ({ fieldsLength, isCompleted, statusError }: IGetIconProps): ElementType => {
  if (isCompleted && !statusError && fieldsLength) {
    return SidebarCheckIcon
  }
  if (statusError) {
    return SidebarErrorIcon
  }
  return SidebarEmptyIcon
}

interface IStepperLabelProps {
  label: string
  values: Record<string, any>
  errors: Record<string, any>
  fieldsLength: number
  isActive: boolean
}

export const SidebarStepLabel: FC<IStepperLabelProps> = ({
  errors,
  fieldsLength,
  isActive,
  label,
  values,
}) => {
  const { error: statusError, isCompleted } = getStatus(values, errors)
  const stopLabelClasses = useStepLabelStyles({
    error: statusError,
    completed: isCompleted,
    isActive,
  })
  const icon = getIcon({ isCompleted, statusError, fieldsLength })

  return (
    <StepLabel
      classes={stopLabelClasses}
      StepIconComponent={icon}
      StepIconProps={{
        active: isActive,
      }}>
      {label}
    </StepLabel>
  )
}
